import { createElement } from 'react';
import ReactDOM from 'react-dom';
import { parse } from 'wellknown';

import { type Feature } from 'geojson';
import { FavoriteToggler } from '../../FavoriteToggler/FavoriteToggler';
import { FavoriteStorage } from '../../Util/FavoriteStorage';
import { SvgIcon } from '../Extensions/SvgIcon';
import { ClusteredMarkerLayer } from '../Layers/ClusteredMarkersLayer';
import type { ClusteredMarkerLayerOptions } from '../Layers/Layers.types';
import BikeParking from '../PopupTemplates/BikeParking';
import ParkingGbg from '../PopupTemplates/ParkingGbg';
import ShuttleParkingGbg from '../PopupTemplates/ShuttleParkingGbg';
import { ParkingsGbgSource } from '../Sources/ParkingsGbg/ParkingBase';
import { ShuttleParkingsSource } from '../Sources/ParkingsGbg/ShuttleParkingsSource';
import { affectedArea } from './AffectedArea';

function createInformationLayer(feature) {
    if (feature.properties.wKT) {
        return affectedArea(parse(feature.properties.wKT));
    }
}

function parkingBase(name: string, sourceOptions: { apiPath: string }, iconId?: number) {
    const defaultOptions: ClusteredMarkerLayerOptions = {
        name: name,
        itemPlural: 'parkeringar',
        source: new ParkingsGbgSource(sourceOptions.apiPath, iconId),
        createInformationLayer: createInformationLayer,
        persistentInformationLayer: false,
        popupTemplate: ParkingGbg,
        defaultIconId: iconId,
    };

    return function (options?: ClusteredMarkerLayerOptions) {
        return new ClusteredMarkerLayer({ ...defaultOptions, ...options });
    };
}

export const parkingsGbgTime = parkingBase('Tidsbegränsad parkering', { apiPath: 'PublicTimeParkings' }, 210);
export const parkingsGbgChargePublic = parkingBase('Avgiftsbelagd parkering', { apiPath: 'PublicTollParkings' }, 210);
export const parkingsGbgChargePrivate = parkingBase('Avgiftsbelagd parkering', { apiPath: 'PrivateTollParkings' }, 210);
export const parkingsGbgBus = parkingBase('Parkering för buss', { apiPath: 'BusParkings' }, 204);
export const parkingsGbgTruck = parkingBase('Parkering för lastbil', { apiPath: 'TruckParkings' }, 202);
export const parkingsGbgDisabled = parkingBase('Parkering för rörelsehindrade', { apiPath: 'HandicapParkings' }, 201);
export const parkingsGbgMC = parkingBase('Motorcykelparkering', { apiPath: 'MCParkings' }, 203);

export const shuttleParkingGbg = function (options?: ClusteredMarkerLayerOptions): ClusteredMarkerLayer {
    const defaultOptions: ClusteredMarkerLayerOptions = {
        name: 'Pendelparkering',
        itemPlural: 'pendelparkeringar',
        source: new ShuttleParkingsSource({ updateInterval: 60 }),
        popupTemplate: ShuttleParkingGbg,
        defaultIconId: 208,
        onPopupOpen: (e) => {
            setTimeout(function () {
                ReactDOM.render(
                    createElement(FavoriteToggler, {
                        currentItemId: e.target.feature.id.toString(),
                        storageKey: `FavoriteParkingsGBG`,
                    }),
                    document.getElementById('popup-favorite-toggler'),
                );
            }, 200);
        },
        defaultFilter: {
            filterFn: function (features: Feature[]) {
                const key = 'FavoriteParkingsGBG';
                const favorites = new FavoriteStorage(key).getFavorites();
                return features.filter((feature) => favorites.indexOf(feature.id.toString()) > -1);
            },
            order: -1,
        },
    };

    return new ClusteredMarkerLayer({ ...defaultOptions, ...options });
};

export const bikeParkingGbg = function (options?: ClusteredMarkerLayerOptions): ClusteredMarkerLayer {
    const defaultOptions: ClusteredMarkerLayerOptions = {
        name: 'Cykelparkering',
        itemPlural: 'cykelparkeringar',
        source: new ParkingsGbgSource('BikeParkings'),
        createInformationLayer: createInformationLayer,
        persistentInformationLayer: false,
        popupTemplate: BikeParking,
        customIcon: new SvgIcon({
            iconId: 'bike-parking-marker',
            iconSize: [24, 36],
            iconAnchor: [12, 36],
            popupAnchor: [0, -36],
        }),
        customClusterIconId: 'bike-parking-cluster',
    };

    return new ClusteredMarkerLayer({ ...defaultOptions, ...options });
};
